import { createFileRoute, Link } from '@tanstack/react-router'
import { Box, Typography } from '@mui/material'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, Steps, DefList, C, Automate } from '../components/docs/prose'
import { DOCS_NAV } from '../components/docs/nav'

export const Route = createFileRoute('/docs/')({ component: DocsIndex })

const SECTIONS = [
  { id: 'what-it-is', title: 'What FolioWorth is' },
  { id: 'the-rule', title: 'The one rule' },
  { id: 'first-ten-minutes', title: 'Your first ten minutes' },
  { id: 'vocabulary', title: 'Vocabulary' },
  { id: 'map', title: 'Everything here' },
]

function DocsIndex() {
  return (
    <DocPage
      path="/docs"
      title="FolioWorth documentation"
      lede={
        <>
          FolioWorth is a net-worth and bill tracker for Canadian households,
          built on a real double-entry ledger. These pages explain the
          accounting it uses, in plain terms, and then show you how to stop
          doing it by hand.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="what-it-is" title="What FolioWorth is">
        <P>
          Most personal-finance apps store a list of balances and add them up.
          That works until two numbers disagree, and then there is no way to
          find out which one is wrong, because nothing in the data says where
          any dollar came from.
        </P>
        <P>
          FolioWorth stores movements instead of balances. Every balance you
          see — an account, your net worth, what a card owes — is the sum of
          the movements that produced it, recomputed on read. There is no
          cached balance column anywhere in the schema, which is the usual
          place a finance app quietly drifts.
        </P>
        <P>
          The parts you will actually use day to day are the payment grid
          (every payee down, every month across), the money flow map (where
          your income went), and bills, which is where the difference between
          &ldquo;I paid it&rdquo; and &ldquo;the bank took it&rdquo; is
          tracked.
        </P>
      </Section>

      <Section id="the-rule" title="The one rule">
        <P>
          If money is in somewhere, it has to be out somewhere. Every
          transaction records at least two lines, and those lines sum to zero.
          A $200 grocery run is not one fact, it is two: groceries went up
          $200, and the Visa went down $200.
        </P>
        <P>
          This is not a convention the app tries to follow. It is a database
          constraint. An entry that does not balance cannot be committed, so
          there is no state of the system in which your money is unaccounted
          for. <Link to="/docs/double-entry" style={{ color: 'var(--accent-text)' }}>
            The double-entry page
          </Link>{' '}
          works through what that buys you.
        </P>
      </Section>

      <Section id="first-ten-minutes" title="Your first ten minutes">
        <Steps
          items={[
            {
              title: 'Open the demo instead',
              body: (
                <>
                  If you have not decided yet, the <Link to="/demo" style={{ color: 'var(--accent-text)' }}>demo</Link>{' '}
                  is a real household with two years of history, cards that
                  revolve, and a payment that never posted. It resets every
                  fifteen minutes, so nothing you do there matters.
                </>
              ),
            },
            {
              title: 'Add the accounts you actually watch',
              body: (
                <>
                  Chequing, the cards, the mortgage, the registered accounts.
                  Three types exist: <C>Asset</C>, <C>Investment</C> and{' '}
                  <C>Debt</C>. You do not create the bookkeeping accounts —
                  FolioWorth makes those itself the first time it needs one.
                </>
              ),
            },
            {
              title: 'Give each account an opening balance',
              body: (
                <>
                  The starting balance has to come from somewhere, and in
                  double-entry that somewhere is equity. It is posted against{' '}
                  <C>Equity: Opening Balances</C>, which keeps the entry
                  balanced without inventing income you never earned.
                </>
              ),
            },
            {
              title: 'Set up the bills you care about',
              body: (
                <>
                  A payee is the stable thing (Hydro, the Amex). A bill is one
                  month of it. You do not need to backfill years of them —
                  start with this month.
                </>
              ),
            },
            {
              title: 'Connect a bank, or import a CSV',
              body: (
                <>
                  This is the step that decides whether you keep using the app.
                  Manual entry is supported and correct, and nobody sustains
                  it. See{' '}
                  <Link to="/docs/automating" style={{ color: 'var(--accent-text)' }}>
                    Automating it
                  </Link>.
                </>
              ),
            },
          ]}
        />
      </Section>

      <Section id="vocabulary" title="Vocabulary">
        <P>
          Five words carry most of the meaning in the rest of these docs.
        </P>
        <DefList
          items={[
            ['Journal entry', 'One economic event. Has a date, a description, a source, and two or more lines.'],
            ['Ledger line', 'One leg of an entry: an account, a signed amount, and optionally a category. Positive means value arrived in that account.'],
            ['Account', 'Anything a line can point at. Includes the things you own and owe, and the bookkeeping counterparties.'],
            ['Payee', 'A recurring biller. Carries the stable facts: category, which account funds it, which liability it settles.'],
            ['Bill', 'One month of one payee. Carries the per-month facts: due date, what was owed, what was paid, and whether the bank agreed.'],
          ]}
        />
      </Section>

      <Section id="map" title="Everything here">
        <Box sx={{ mt: 2 }}>
          {DOCS_NAV.flatMap((s) => s.pages)
            .filter((p) => p.path !== '/docs')
            .map((page) => (
              <Box
                key={page.path}
                component={Link}
                to={page.path}
                sx={{
                  display: 'block', py: 1.75,
                  borderTop: '1px solid var(--border-soft)',
                  textDecoration: 'none',
                }}
              >
                <Typography sx={{ fontWeight: 600, color: 'var(--text)' }}>
                  {page.title}
                </Typography>
                <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.94rem', lineHeight: 1.6 }}>
                  {page.summary}
                </Typography>
              </Box>
            ))}
        </Box>
      </Section>

      <Automate>
        Read these in order once, then never again. The point of the accounting
        is that you should not have to think about it: connect an account, and
        the entries below get written for you with both legs already correct.
      </Automate>
    </DocPage>
  )
}
